'use client';

import { ICartItem, ITotalData } from '@/api/types/cart';
import Button from '@/components/Button';
import Image from '@/components/Image';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { useStore } from '@/providers/app-store-provider';
import { IS_DE_FR } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { CollapseArrowUpOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useState } from 'react';
import { OrderSummary } from './OrderSummary';

interface CartBottomBarProps {
  // 基础数据
  selectedItems: ICartItem[];
  totalData: ITotalData;
  allCheck: boolean;
  onAllCheckChange: (checked: boolean) => void;

  // 结账相关
  checkoutLoading: boolean;
  onCheckout: () => void;

  // 批量管理模式
  batchFlag: boolean;
  batchDisabled?: boolean;
  onMoveToWish: () => void;
  onBatchDelete: () => void;

  // 显示控制
  show: boolean;

  // 优惠券提示
  maxSaving?: string;

  // 第二件半价数据
  halfPriceData?: {
    status?: number;
    title?: string;
    content?: string;
    rule?: {
      rule_flag?: number;
      discount_rate?: string;
      discount_text?: string;
      threshold_price?: number;
      restrict_single?: number;
    };
  };
}

export function CartBottomBar({
  selectedItems,
  totalData,
  allCheck,
  onAllCheckChange,
  checkoutLoading,
  onCheckout,
  batchFlag,
  batchDisabled = false,
  onMoveToWish,
  onBatchDelete,
  show,
  maxSaving,
  halfPriceData,
}: CartBottomBarProps) {
  const t = useTranslations('cart.bottomBar');
  const { currency } = useStore((state) => state);
  const [showOrderSummary, setShowOrderSummary] = useState(false);

  const totalItems = selectedItems.reduce((sum, item) => sum + (item.qty || 0), 0);
  const hasDiscount = totalData.discount && totalData.discount !== '0.00';

  if (!show) {
    return null;
  }

  // 计算按钮是否应该显示为禁用状态
  const shouldShowDisabledStyle = selectedItems.length === 0; // 只有没选商品时显示禁用样式，但仍可点击

  return (
    <>
      {/* 满赠横幅提示栏 - 优先级高于优惠券提示 */}
      {selectedItems.length > 0 &&
        !batchFlag &&
        totalData.show_gift?.is_satisfied === 2 &&
        totalData.show_gift?.copy_writing &&
        totalData.show_gift?.gift_image && (
          <div
            className="fixed bottom-[69px] left-0 right-0 bg-[rgba(40,40,40,0.9)] text-white flex items-center px-[8px] z-[12]"
            style={{
              height: '48px',
              boxShadow: '0px -2px 4px rgba(204,204,204,0.3)',
            }}
          >
            <Image
              src={totalData.show_gift.gift_image}
              alt=""
              width={40}
              height={40}
              className="w-[40px] h-[40px] mr-[12px] rounded-[4px] object-cover"
            />
            <span
              className="text-[13px] flex-1 font-bb line-clamp-2"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              dangerouslySetInnerHTML={{
                __html: totalData.show_gift.copy_writing,
              }}
            />
          </div>
        )}

      {/* 优惠券提示栏 - 仅在未显示满赠横幅时显示 */}
      {selectedItems.length > 0 &&
        maxSaving &&
        !batchFlag &&
        totalData.show_gift?.is_satisfied !== 2 && (
          <div
            className="fixed bottom-[69px] left-0 right-0 bg-[#e9ffbc] px-[14px] py-[8px] z-[12]"
            style={{ boxShadow: '0px -2px 4px rgba(204,204,204,0.3)' }}
          >
            <div className="flex items-center">
              <Image
                src="/cart/sale.png"
                alt="max save"
                width={20}
                height={20}
                className="w-[20px] h-[20px] mr-[6px] flex-shrink-0"
              />
              <span
                className="text-[12px] flex-1 font-bs"
                dangerouslySetInnerHTML={{ __html: maxSaving }}
              />
            </div>
          </div>
        )}

      <div
        className="fixed bottom-0 left-0 right-0 bg-white z-[365]"
        style={{ boxShadow: '0px -2px 4px rgba(204,204,204,0.5)' }}
      >
        {/* 正常结算模式 */}
        {!batchFlag && (
          <div className="flex items-center px-[14px] h-[69px]">
            {/* 全选区域 */}
            <div className="flex items-center">
              <ZeeloolCheckBox
                checked={allCheck}
                onChange={(e) => onAllCheckChange(e.target.checked)}
                fontSize={16}
              />
              <span className="text-[14px] ml-[6px]">{t('all')}</span>
            </div>

            {/* 价格显示区域 */}
            <div className="flex flex-col items-start ml-[20px] flex-1">
              <div className={`text-[16px] font-bb`}>
                {formatPriceNumberWithSymbol(totalData.subtotal || '0.00', currency)}
              </div>

              {/* 折扣信息 - 点击弹出详情 */}
              {hasDiscount && (
                <div
                  onClick={() => setShowOrderSummary(!showOrderSummary)}
                  className="flex items-center text-[12px]"
                >
                  <span>
                    {t('discount')} {formatPriceNumberWithSymbol(totalData.discount, currency)}
                  </span>
                  <CollapseArrowUpOutlined
                    className={`text-[12px] ml-[5px] text-[#a9a9a9] transition-transform duration-300 ${showOrderSummary ? 'rotate-180' : ''}`}
                  />
                </div>
              )}
            </div>

            {/* 结账按钮区域 */}
            <div className="relative">
              <Button
                onClick={onCheckout}
                disabled={checkoutLoading} // 只有loading时才真正禁用
                loading={checkoutLoading}
                variant="contained"
                color="primary"
                className="w-[136px] h-[40px] text-[13px] !font-bb"
                style={env.NEXT_PUBLIC_SITE_NAME === 'fr' ? { lineHeight: 1.2 } : {}}
                sx={{
                  '&:disabled': {
                    color: '#ffffff',
                  },
                  '&.Mui-disabled': {
                    color: '#ffffff',
                  },
                  // 没有选中商品时的禁用样式（但仍可点击）
                  ...(shouldShowDisabledStyle &&
                    !checkoutLoading && {
                      opacity: 0.2,
                    }),
                }}
              >
                {t('checkout')}
                {totalItems > 0 ? ` (${totalItems})` : ''}
              </Button>
              {/* 赠品图标 - 当 is_satisfied === 1 时显示 */}
              {totalData.show_gift?.is_satisfied === 1 && totalData.show_gift?.gift_image && (
                <div className="absolute top-[-49px] right-[6px] z-10">
                  <div className="relative p-[5px] rounded-[4px] bg-[#baacff]">
                    <Image
                      src={totalData.show_gift.gift_image}
                      alt=""
                      width={40}
                      height={40}
                      className="block rounded-[4px] w-[40px] h-[40px] object-cover"
                    />
                    {/* 三角形箭头 */}
                    <div className="absolute bottom-[-5px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#baacff]" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 批量管理模式 */}
        {batchFlag && (
          <div className="flex items-center justify-between px-[14px] h-[69px]">
            {/* 全选区域 */}
            <div className="flex items-center">
              <ZeeloolCheckBox
                checked={allCheck}
                onChange={(e) => onAllCheckChange(e.target.checked)}
                fontSize={16}
              />
              <span className="text-[14px] ml-[6px]">{t('all')}</span>
            </div>

            {/* 管理按钮区域 */}
            <div className="flex items-center space-x-3">
              <Button
                onClick={onMoveToWish}
                disabled={batchDisabled}
                variant="outlined"
                className="px-[10px] h-[30px] text-[13px] !mr-[12px]"
                radiusSize="large"
                style={
                  IS_DE_FR
                    ? {
                        lineHeight: 1.2,
                        fontSize: 12,
                        width: 130,
                      }
                    : {}
                }
                sx={{
                  '&:disabled': {
                    borderColor: '#463ae8',
                    color: '#463ae8',
                    opacity: 0.2,
                  },
                  '&.Mui-disabled': {
                    borderColor: '#463ae8',
                    color: '#463ae8',
                    opacity: 0.2,
                  },
                }}
              >
                {t('moveToWishlist')}
              </Button>

              <Button
                onClick={onBatchDelete}
                disabled={batchDisabled}
                variant="outlined"
                className="h-[30px] w-[82px] text-[13px]"
                radiusSize="large"
                sx={{
                  '&:disabled': {
                    borderColor: '#463ae8',
                    color: '#463ae8',
                    opacity: 0.2,
                  },
                  '&.Mui-disabled': {
                    borderColor: '#463ae8',
                    color: '#463ae8',
                    opacity: 0.2,
                  },
                }}
                style={
                  IS_DE_FR
                    ? {
                        lineHeight: 1.2,
                        fontSize: 12,
                      }
                    : {}
                }
              >
                {t('delete')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 订单摘要弹层 */}
      <OrderSummary
        show={showOrderSummary}
        totalData={totalData}
        onClose={() => setShowOrderSummary(false)}
        deviceType={EPlatform.WAP}
        halfPriceData={halfPriceData}
      />
    </>
  );
}
